import React, { useEffect, useMemo, useState } from 'react'
import Footer from '../../../components/Footer/Footer'
import { AuthenticatedHeader } from '../../../components/Header/AuthenticatedHeader'
import classes from './users.module.css'
import gridClasses from '../../../styles/grid.module.css'
import classNames from 'classnames'
import { Button } from '../../../components/Button/Button'
import { ReactComponent as SearchIcon } from '../../../assets/icons/search.svg'
import UsersTable from '../../../components/Tables/UsersTable'
import { InviteForm } from '../../../components/Form/InviteForm/InviteForm'
import { useDispatch, useSelector } from 'react-redux'
import { businessUsersSelectors } from './businessUsersSlice'
import { getBusinessBeneficiaries } from './businessUsersApi'
import { userSelectors } from '../../../redux/userSlice'
import notification from '../../../components/Notification/notification'
import { BusinessUsersRegisteredLoader } from '../../../components/loaders/BusinessUsersLoader'
import { CSVLink } from 'react-csv'
import moment from 'moment'
import { startUpgradePlanWizard } from '../../Wizard/wizardLogic'

const initialFilters = [
    {
        id: 'all',
        text: 'All Users',
        number: 0,
    },
    {
        id: 'Active',
        text: 'Registered Users',
    },
    {
        id: 'Pending',
        text: 'Pending Invitations',
    },
    {
        id: 'Suspended',
        text: 'Deactivated Users',
    },
]

const csvHeaders = [
    {
        label: 'Name',
        key: 'name',
    },
    {
        label: 'Email',
        key: 'email',
    },
    {
        label: 'Status',
        key: 'status',
    },
    {
        label: 'Enrolled Courses',
        key: 'enrolled',
    },
    {
        label: 'Date Added',
        key: 'date',
    },
]

const Users = () => {
    const [formOpen, showForm] = useState(false)

    const dispatch = useDispatch()

    const users = useSelector(businessUsersSelectors.users)
    const hasError = useSelector(businessUsersSelectors.hasError)
    const errorMessage = useSelector(businessUsersSelectors.errorMessage)
    const isFetching = useSelector(businessUsersSelectors.isFetching)

    const user = useSelector(userSelectors.userDetails)

    useEffect(() => {
        dispatch(getBusinessBeneficiaries(user.email))
    }, [user.email])

    const [filters, setFilters] = useState(initialFilters)

    useEffect(() => {
        if (!isFetching) {
            if (hasError) {
                notification.failure({
                    title: 'There was an issue fetching your beneficiaries',
                    description: errorMessage,
                })
            } else {
                const newFilters = [...filters]
                newFilters[0].number = users.length
                setFilters(newFilters)
            }
        }
    }, [isFetching])

    const [activeFilter, setActiveFilter] = useState('all')

    const [search, setSearch] = useState('')

    const validUsers = useMemo(() => {
        let validUsers = users
        switch (activeFilter) {
            case 'Active': {
                validUsers = validUsers.filter(
                    ({ name, status }) => name && status !== 'Deactivate'
                )
                break
            }
            case 'Pending': {
                validUsers = validUsers.filter(({ name }) => !name)
                break
            }
            case 'Suspended': {
                validUsers = validUsers.filter(
                    ({ status }) => status === 'Deactivate'
                )
                break
            }
            default: {
                break
            }
        }

        if (search) {
            validUsers = validUsers.filter(({ name }) =>
                name?.toLowerCase().includes(search)
            )
        }

        return validUsers
    }, [activeFilter, users, search])

    const registeredUsers = useMemo(
        // () => users.filter(({ status }) => status === 'Active').length,
        //TODO Change to check status pending
        () => users.filter(({ name }) => name).length,
        [users]
    )

    const changePlan = () => {
        startUpgradePlanWizard({ returnURL: '/app/browse/courses' })
    }

    const csvData = useMemo(() => {
        return users.map(user => ({
            ...user,
            date: moment(user.dateadded).format('Do MMMM, YYYY'),
            name: user.name || 'No Name',
        }))
    }, [users])

    const csvFilename = useMemo(
        () =>
            `SME Upturn Business Users for ${user.firstname ||
                ''} ${user.lastname || ''}.csv`,
        [user.firstname, user.lastname]
    )

    return (
        <>
            <AuthenticatedHeader isBusinessOwner={true} />
            <div className={classNames(classes.top, gridClasses.container)}>
                <div>Manage Users</div>
            </div>
            <main
                className={classNames(gridClasses.container, classes.container)}
            >
                <aside>
                    {isFetching ? (
                        <BusinessUsersRegisteredLoader
                            className={classes.planLoader}
                        />
                    ) : (
                        <div className={classes.plan}>
                            <h3 className={classes.planHeader}>
                                {registeredUsers} of {users.length} users
                                registered
                            </h3>
                            <button
                                className={classes.planChange}
                                onClick={changePlan}
                            >
                                Change Plan
                            </button>
                        </div>
                    )}
                    <UsersSidebar
                        activeFilter={activeFilter}
                        setActiveFilter={setActiveFilter}
                        filters={filters}
                    />
                    <UsersTopBar
                        activeFilter={activeFilter}
                        setActiveFilter={setActiveFilter}
                        filters={filters}
                    />
                </aside>
                <div>
                    <div className={classes.usersTableTop}>
                        <h3 className={classes.usersTableTopHeader}>
                            All Users
                        </h3>
                        {/*TODO Use button component*/}
                        <CSVLink
                            className={classNames(
                                classes.usersTableTopButton,
                                classes.usersTableTopExportButton
                            )}
                            headers={csvHeaders}
                            data={csvData}
                            filename={csvFilename}
                        >
                            Export CSV
                        </CSVLink>
                        <Button
                            className={classes.usersTableTopButton}
                            onClick={() => showForm(true)}
                        >
                            Invite New User
                        </Button>
                    </div>
                    <div className={classes.usersTableContentModifiers}>
                        <div>
                            <input
                                type="text"
                                className={
                                    classes.usersTableContentModifiersInput
                                }
                                placeholder={
                                    'Search for a user by name or username'
                                }
                                value={search}
                                onChange={({ target: { value } }) =>
                                    setSearch(value)
                                }
                            />
                            <SearchIcon
                                className={
                                    classes.usersTableContentModifiersInputIcon
                                }
                            />
                        </div>
                        {/*TODO: Sort by*/}
                        {/*<span*/}
                        {/*    className={classes.usersTableContentModifiersSortBy}*/}
                        {/*>*/}
                        {/*    Sort By: None*/}
                        {/*</span>*/}
                    </div>
                    <UsersTable users={validUsers} isFetching={isFetching} />
                    <InviteForm open={formOpen} onRequestClose={showForm} />
                </div>
            </main>
            <Footer />
        </>
    )
}

const UsersSidebar = ({ activeFilter, setActiveFilter, filters }) => {
    return (
        <div className={classes.sidebar}>
            {filters.map(({ id, text, number = 0 }) => (
                <button
                    key={id}
                    onClick={() => setActiveFilter(id)}
                    className={classNames(
                        classes.sidebarLink,
                        id === activeFilter && classes.sidebarLinkActive
                    )}
                >
                    {text}
                    {number > 0 && (
                        <span className={classes.sidebarLinkNumber}>
                            {number}
                        </span>
                    )}
                </button>
            ))}
        </div>
    )
}

const UsersTopBar = ({ activeFilter, setActiveFilter, filters }) => {
    return (
        <div className={classes.topBar}>
            {filters.map(({ text, number, id }) => (
                <button
                    className={classNames(
                        classes.topBarButton,
                        activeFilter === id && classes.topBarButtonActive
                    )}
                    key={id}
                    onClick={() => setActiveFilter(id)}
                >
                    {text}
                    {number > 0 && (
                        <span className={classes.topBarButtonNumber}>
                            {number}
                        </span>
                    )}
                </button>
            ))}
        </div>
    )
}

export default Users
